// URL <-> state helpers + filter logic for the mentor directory (Home `/`). Pure module,
// fully unit-testable. Filter semantics: AND across categories, OR within a multi-select.
//
// NOTE: the brief's fee-range filter is omitted — the public mentor list
// (listPublicMentors / RealMentor) has no per-mentor fee field (priceInr lives on courses,
// not mentors). Revisit if/when mentors expose a fee.
import { ALLOWED_EXAM_TAGS, EXAM_TAG_LABEL, type ExamTag } from './examTags';
import { CITY_BY_SLUG, cityMatchesName } from './cities';
import { consultationMatches, type ConsultationType } from './consultation';
import { SPORT_OPTIONS } from './sports';
import { HOBBY_OPTIONS } from './hobbies';

export type Mode = 'online' | 'in-person' | 'hybrid';
export const ALL_MODES: Mode[] = ['online', 'in-person', 'hybrid'];
// Common qualification facets (matched as case-insensitive substrings of the free-text
// qualifications array, since mentors type e.g. "M.Sc Physics", "B.Ed").
export const QUALIFICATION_OPTIONS = ['PhD', 'M.Sc', 'M.Tech', 'B.Tech', 'M.A', 'B.Ed', 'MBBS', 'MD', 'CA'];
// Exam-label values that mark a mentor as a counsellor (they don't teach a specific exam).
// Kept in sync with the counselling options in ProfileSetup's EXAM_OPTIONS.
export const COUNSELLING_EXAM_LABELS = ['counselling', 'career counselling', 'admission counselling'];

// Mentor directory category buckets — the four active tracks. The slug list MUST stay
// byte-identical across six places (ProfileSetup.MENTOR_CATEGORIES, this file,
// AdminMentorEdit.CATEGORY_OPTIONS, updateMentorAdmin.VALID_CATEGORIES, listPublicMentors,
// Home.CategoryBadge) or a mentor can be selected-but-not-filterable.
// mentorCategories.test.ts asserts that across all six files.
export const MENTOR_CATEGORIES = ['academic', 'skills', 'hobbies', 'spiritual'] as const;
export type MentorCategory = (typeof MENTOR_CATEGORIES)[number];
export const MENTOR_CATEGORY_LABEL: Record<MentorCategory, string> = {
  academic: 'Academics', skills: 'Career Skills', hobbies: 'Hobbies & Games', spiritual: 'Spiritual',
};

// Retired buckets, mapped to their replacement. Kept as a READ-TIME alias rather than a
// data migration: an old record (or a bookmarked ?cat=aviation URL) resolves into the right
// active bucket without touching a single mentor document. `hobbies` is deliberately NOT
// retired — it is reused as the Hobbies & Games bucket, so those mentors need no migration.
export const RETIRED_CATEGORY_ALIAS: Record<string, MentorCategory> = {
  aviation: 'skills',
  language: 'academic',
  // `others` deliberately has NO alias. It was a catch-all holding academics and career
  // trainers, not hobbyists — mapping it to any single bucket put a visibly wrong badge on
  // real mentors. Unmapped means "no badge", and the UI infers a bucket from content
  // instead (inferTrackFromContent in lib/tracks.ts).
};

/** Any stored/URL category → an active slug, or null when unrecognised. */
export function normalizeCategory(v: unknown): MentorCategory | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if ((MENTOR_CATEGORIES as readonly string[]).includes(s)) return s as MentorCategory;
  return RETIRED_CATEGORY_ALIAS[s] ?? null;
}

// Mentor gender (optional profile field). 'other' covers non-binary / prefer-not-to-say.
export const GENDERS = ['male', 'female', 'other'] as const;
export type Gender = (typeof GENDERS)[number];
export const GENDER_LABEL: Record<Gender, string> = { male: 'Male', female: 'Female', other: 'Other' };
const VALID_GENDERS = new Set<string>(GENDERS);

export interface MentorFilterState {
  // `| undefined` (not just `?`) so callers can spread-and-clear under the repo's
  // exactOptionalPropertyTypes: { ...state, city: undefined }.
  city?: string | undefined;                 // CITY slug
  exams?: ExamTag[] | undefined;             // multi
  modes?: Mode[] | undefined;                // multi
  minYearsExperience?: number | undefined;
  minRating?: number | undefined;            // e.g. 4, 4.5, 5
  qualifications?: string[] | undefined;     // multi
  consultation?: ConsultationType | undefined; // free | paid | both (see consultationMatches)
  counsellor?: boolean | undefined;          // only mentors who offer counselling
  category?: MentorCategory | undefined;     // directory category bucket
  subjects?: string[] | undefined;           // subject labels (multi, OR)
  hobbies?: string[] | undefined;            // hobby labels (multi, OR)
  sports?: string[] | undefined;             // game labels (multi, OR)
  gender?: Gender | undefined;               // mentor gender
  offering?: Offering | undefined;           // content-derived Games / Hobbies bucket
  q?: string | undefined;                    // free-text search (name / subject / exam / tagline / city / state)
}

// "Games" and "Hobbies" aren't stored categories — they're derived from what a mentor put
// in their Exams field (games/hobbies options, or the umbrella "Sports Coaching" /
// "Extra-Curricular Activities" tags). Surfaced as extra Category-filter chips + card badges.
export const OFFERINGS = ['games', 'hobbies'] as const;
export type Offering = (typeof OFFERINGS)[number];
export const OFFERING_LABEL: Record<Offering, string> = { games: '⚽ Games', hobbies: '🎨 Hobbies' };
const VALID_OFFERINGS = new Set<string>(OFFERINGS);
type OfferingMentor = { offering?: string | null; examsTaught?: readonly string[]; subjectsTaught?: readonly string[]; sportsTaught?: readonly string[]; hobbies?: readonly string[] };

// Minimal mentor shape the filter reads (subset of RealMentor from listPublicMentors).
export interface DirectoryMentor {
  city: string | null;
  state?: string | null;
  examsTaught: string[];         // LABELS, e.g. ['JEE Main']
  teachingMode: string[];        // stored as 'online' | 'in_person' | 'hybrid'
  yearsTeachingExperience: number;
  ratingAvg: number;
  qualifications: string[];
  consultation: ConsultationType;
  category?: string | null;
  subjectsTaught?: string[];     // LABELS, e.g. ['Physics']
  hobbies?: string[];            // LABELS, e.g. ['Guitar']
  sportsTaught?: string[];       // LABELS, e.g. ['Cricket']
  offering?: string | null;      // explicit admin Games/Hobbies tag (advertising.offering)
  displayName?: string;
  tagline?: string | null;
  gender?: string | null;
}

const VALID_EXAMS = new Set<string>(ALLOWED_EXAM_TAGS);
const norm = (s: string) => String(s ?? '').trim().toLowerCase(); // defensive: a non-string in examsTaught/subjects (bad record) must not throw
const normMode = (m: string): string => norm(m).replace(/_/g, '-'); // in_person -> in-person

function csv(v: string | null): string[] {
  return (v ?? '').split(',').map((s) => s.trim()).filter(Boolean);
}

// Game/hobby SIGNALS include the umbrella exam options, so a mentor who just picks
// "Sports Coaching" or "Extra-Curricular Activities" still buckets correctly.
const GAME_SIGNALS = new Set([...SPORT_OPTIONS, 'Sports Coaching', 'Sports Quota / Trials'].map(norm));
const HOBBY_SIGNALS = new Set([...HOBBY_OPTIONS, 'Extra-Curricular Activities'].map(norm));

// Games/hobbies can now live in Exams OR Subjects, so scan both (plus the dedicated
// sportsTaught/hobbies arrays), and a mentor buckets correctly wherever they tagged it.
/** A mentor "does games" if any of their exam/subject/sports labels is a game or the sports umbrella. */
export function mentorOffersGames(m: OfferingMentor): boolean {
  if (m.offering === 'games') return true; // explicit admin tag wins
  return [...(m.examsTaught ?? []), ...(m.subjectsTaught ?? []), ...(m.sportsTaught ?? [])].some((x) => GAME_SIGNALS.has(norm(x)));
}
/** A mentor "does hobbies" if any of their exam/subject/hobby labels is a hobby or the co-curricular umbrella. */
export function mentorOffersHobbies(m: OfferingMentor): boolean {
  if (m.offering === 'hobbies') return true; // explicit admin tag wins
  return [...(m.examsTaught ?? []), ...(m.subjectsTaught ?? []), ...(m.hobbies ?? [])].some((x) => HOBBY_SIGNALS.has(norm(x)));
}

// --- URL <-> state ---------------------------------------------------------

export function parseFilters(sp: URLSearchParams): MentorFilterState {
  const out: MentorFilterState = {};
  // City is free-text now (accept any non-empty value): mentors live in cities beyond the
  // fixed SEO list, and the directory filter is built from real mentor cities. A known SEO
  // slug still matches via cityMatchesName in applyFilters; any other value matches by name.
  const city = sp.get('city');
  if (city) out.city = city;

  const exams = csv(sp.get('exams')).filter((e): e is ExamTag => VALID_EXAMS.has(e));
  if (exams.length) out.exams = exams;

  const modes = csv(sp.get('modes')).map(normMode).filter((m): m is Mode => (ALL_MODES as string[]).includes(m));
  if (modes.length) out.modes = modes as Mode[];

  const minYrs = Number(sp.get('minYrs'));
  if (Number.isFinite(minYrs) && minYrs > 0) out.minYearsExperience = minYrs;

  const minRating = Number(sp.get('minRating'));
  if (Number.isFinite(minRating) && minRating > 0) out.minRating = minRating;

  const quals = csv(sp.get('quals'));
  if (quals.length) out.qualifications = quals;

  const consultation = sp.get('consultation');
  if (consultation === 'free' || consultation === 'paid' || consultation === 'both') out.consultation = consultation;

  if (sp.get('counsellor') === '1') out.counsellor = true;

  // Retired slugs in a bookmarked URL (?cat=aviation) resolve to their active bucket.
  const cat = normalizeCategory(sp.get('cat'));
  if (cat) out.category = cat;

  const subjects = csv(sp.get('subjects'));
  if (subjects.length) out.subjects = subjects;

  const hobbies = csv(sp.get('hobbies'));
  if (hobbies.length) out.hobbies = hobbies;

  const sports = csv(sp.get('sports'));
  if (sports.length) out.sports = sports;

  const gender = sp.get('gender');
  if (gender && VALID_GENDERS.has(gender)) out.gender = gender as Gender;

  const offering = sp.get('offering');
  if (offering && VALID_OFFERINGS.has(offering)) out.offering = offering as Offering;

  const q = sp.get('q') ?? '';
  if (q.trim()) out.q = q.slice(0, 80);

  return out;
}

// → a plain object suitable for react-router setSearchParams (omits empties).
export function serializeFilters(state: MentorFilterState): Record<string, string> {
  const p: Record<string, string> = {};
  if (state.city) p.city = state.city;
  if (state.exams?.length) p.exams = state.exams.join(',');
  if (state.modes?.length) p.modes = state.modes.join(',');
  if (state.minYearsExperience) p.minYrs = String(state.minYearsExperience);
  if (state.minRating) p.minRating = String(state.minRating);
  if (state.qualifications?.length) p.quals = state.qualifications.join(',');
  if (state.consultation) p.consultation = state.consultation;
  if (state.counsellor) p.counsellor = '1';
  if (state.category) p.cat = state.category;
  if (state.subjects?.length) p.subjects = state.subjects.join(',');
  if (state.hobbies?.length) p.hobbies = state.hobbies.join(',');
  if (state.sports?.length) p.sports = state.sports.join(',');
  if (state.gender) p.gender = state.gender;
  if (state.offering) p.offering = state.offering;
  if (state.q) p.q = state.q;
  return p;
}

export function hasActiveFilters(s: MentorFilterState): boolean {
  return Object.keys(serializeFilters(s)).length > 0;
}

// --- filter logic (AND across categories, OR within multi-select) ----------

export function applyFilters<T extends DirectoryMentor>(mentors: T[], s: MentorFilterState): T[] {
  return mentors.filter((m) => {
    // Normalised both sides: a mentor still stored as 'aviation' matches the 'skills' filter.
    if (s.category && normalizeCategory(m.category) !== s.category) return false;
    if (s.gender && (m.gender ?? '') !== s.gender) return false;
    if (s.offering === 'games' && !mentorOffersGames(m)) return false;
    if (s.offering === 'hobbies' && !mentorOffersHobbies(m)) return false;
    if (s.subjects?.length) {
      const subs = new Set((m.subjectsTaught ?? []).map(norm));
      if (!s.subjects.some((x) => subs.has(norm(x)))) return false;
    }
    if (s.hobbies?.length) {
      const hob = new Set((m.hobbies ?? []).map(norm));
      if (!s.hobbies.some((x) => hob.has(norm(x)))) return false;
    }
    if (s.sports?.length) {
      const spt = new Set((m.sportsTaught ?? []).map(norm));
      if (!s.sports.some((x) => spt.has(norm(x)))) return false;
    }
    if (s.q) {
      const needle = norm(s.q);
      const hay = norm([m.displayName ?? '', (m.subjectsTaught ?? []).join(' '), (m.examsTaught ?? []).join(' '), (m.hobbies ?? []).join(' '), (m.sportsTaught ?? []).join(' '), m.tagline ?? '', m.city ?? '', m.state ?? ''].join(' '));
      if (!hay.includes(needle)) return false;
    }
    if (s.city) {
      // Known SEO slug → match by the city's canonical name + aliases (deep-links from
      // landing pages). Otherwise s.city is a real city NAME from the directory dropdown →
      // exact normalized-name match, so any mentor city (incl. non-SEO towns) filters.
      const cityCfg = CITY_BY_SLUG[s.city];
      const ok = cityCfg ? cityMatchesName(cityCfg, m.city) : norm(m.city ?? '') === norm(s.city);
      if (!ok) return false;
    }
    if (s.exams?.length) {
      const want = new Set(s.exams.map((e) => norm(EXAM_TAG_LABEL[e])));
      if (!(m.examsTaught ?? []).some((label) => want.has(norm(label)))) return false;
    }
    if (s.modes?.length) {
      const mentorModes = new Set((m.teachingMode ?? []).map(normMode));
      if (!s.modes.some((mode) => mentorModes.has(mode))) return false;
    }
    if (s.minYearsExperience && (m.yearsTeachingExperience ?? 0) < s.minYearsExperience) return false;
    if (s.minRating && (m.ratingAvg ?? 0) < s.minRating) return false;
    if (s.qualifications?.length) {
      const quals = (m.qualifications ?? []).map(norm);
      if (!s.qualifications.some((q) => quals.some((mq) => mq.includes(norm(q))))) return false;
    }
    if (s.consultation && !consultationMatches(s.consultation, m.consultation)) return false;
    if (s.counsellor) {
      const ex = (m.examsTaught ?? []).map(norm);
      if (!COUNSELLING_EXAM_LABELS.some((c) => ex.includes(c))) return false;
    }
    return true;
  });
}

// --- active-filter chips ---------------------------------------------------

export interface FilterChip { key: string; label: string; next: MentorFilterState }

// Each chip, when applied, returns the state with that filter value removed.
export function activeChips(s: MentorFilterState): FilterChip[] {
  const chips: FilterChip[] = [];
  if (s.city) chips.push({ key: `city`, label: CITY_BY_SLUG[s.city]?.displayName ?? s.city, next: { ...s, city: undefined } });
  for (const e of s.exams ?? []) {
    chips.push({ key: `exam:${e}`, label: EXAM_TAG_LABEL[e], next: { ...s, exams: s.exams!.filter((x) => x !== e) } });
  }
  for (const mode of s.modes ?? []) {
    chips.push({ key: `mode:${mode}`, label: mode === 'in-person' ? 'In-person' : mode[0]!.toUpperCase() + mode.slice(1), next: { ...s, modes: s.modes!.filter((x) => x !== mode) } });
  }
  if (s.minYearsExperience) chips.push({ key: 'minYrs', label: `${s.minYearsExperience}+ yrs exp`, next: { ...s, minYearsExperience: undefined } });
  if (s.minRating) chips.push({ key: 'minRating', label: `${s.minRating}★ & up`, next: { ...s, minRating: undefined } });
  for (const q of s.qualifications ?? []) {
    chips.push({ key: `qual:${q}`, label: q, next: { ...s, qualifications: s.qualifications!.filter((x) => x !== q) } });
  }
  if (s.consultation) {
    const label = s.consultation === 'free' ? 'Free consultation' : s.consultation === 'paid' ? 'Paid consultation' : 'Both available';
    chips.push({ key: 'consultation', label, next: { ...s, consultation: undefined } });
  }
  if (s.counsellor) chips.push({ key: 'counsellor', label: 'Counsellors', next: { ...s, counsellor: undefined } });
  if (s.category) chips.push({ key: 'category', label: MENTOR_CATEGORY_LABEL[s.category], next: { ...s, category: undefined } });
  for (const sub of s.subjects ?? []) {
    chips.push({ key: `subject:${sub}`, label: sub, next: { ...s, subjects: s.subjects!.filter((x) => x !== sub) } });
  }
  for (const hob of s.hobbies ?? []) {
    chips.push({ key: `hobby:${hob}`, label: hob, next: { ...s, hobbies: s.hobbies!.filter((x) => x !== hob) } });
  }
  for (const spt of s.sports ?? []) {
    chips.push({ key: `sport:${spt}`, label: spt, next: { ...s, sports: s.sports!.filter((x) => x !== spt) } });
  }
  if (s.gender) chips.push({ key: 'gender', label: GENDER_LABEL[s.gender], next: { ...s, gender: undefined } });
  if (s.offering) chips.push({ key: 'offering', label: OFFERING_LABEL[s.offering], next: { ...s, offering: undefined } });
  if (s.q) chips.push({ key: 'q', label: `“${s.q}”`, next: { ...s, q: undefined } });
  return chips;
}
